// Proposal ORDER CHARGES — web. Mirrors
// backend/src/modules/sales/_shared/proposal-charges.ts. The web bundle cannot
// import backend sources, so the contract is re-declared here and pinned by
// __tests__/charges-totals.test.ts.
//
// A charge is NEVER discounted. Nothing here reads a discount value.

export const CHARGE_TYPES = ['lifting', 'transportation', 'installation'] as const;
export const CHARGE_MODES = ['inclusive', 'client_scope', 'add_amount'] as const;
export const CHARGE_GST_RATES = [0, 5, 12, 18, 28] as const;

export type ChargeType = (typeof CHARGE_TYPES)[number];
export type ChargeMode = (typeof CHARGE_MODES)[number];

export interface ProposalCharge {
  type: ChargeType;
  mode: ChargeMode;
  amount: number;
  gstRatePct: number;
  show: boolean;
}

export interface ChargeTotals {
  chargesSubtotal: number;
  chargesCgst: number;
  chargesSgst: number;
  chargesIgst: number;
  chargesTax: number;
}

const CHARGE_LABELS: Record<ChargeType, string> = {
  lifting: 'Lifting',
  transportation: 'Transportation',
  installation: 'Installation',
};

const MODE_LABELS: Record<ChargeMode, string> = {
  inclusive: 'Inclusive',
  client_scope: "Client's scope",
  add_amount: 'Add amount',
};

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const isType = (v: unknown): v is ChargeType => (CHARGE_TYPES as readonly unknown[]).includes(v);
const isMode = (v: unknown): v is ChargeMode => (CHARGE_MODES as readonly unknown[]).includes(v);

function snapGstRate(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return 18;
  let best: number = CHARGE_GST_RATES[0];
  let bestDist = Math.abs(n - best);
  for (const r of CHARGE_GST_RATES) {
    const d = Math.abs(n - r);
    if (d < bestDist) { best = r; bestDist = d; }
  }
  return best;
}

const posNum = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function normalizeCharges(raw: unknown): ProposalCharge[] {
  if (!Array.isArray(raw)) return [];
  const byType = new Map<ChargeType, ProposalCharge>();
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    if (!isType(o.type) || !isMode(o.mode)) continue;
    const priced = o.mode === 'add_amount';
    byType.set(o.type, {
      type: o.type,
      mode: o.mode,
      amount: priced ? posNum(o.amount) : 0,
      gstRatePct: priced ? snapGstRate(o.gstRatePct) : 0,
      show: priced ? true : o.show === true,
    });
  }
  return CHARGE_TYPES.map((t) => byType.get(t)).filter(Boolean) as ProposalCharge[];
}

export function chargeIsPrinted(c: ProposalCharge): boolean {
  return c.mode === 'add_amount' ? true : c.show === true;
}

export function computeChargeTotals(
  charges: ProposalCharge[],
  supplyType: 'intra_state' | 'inter_state',
): ChargeTotals {
  let sub = 0, cgst = 0, sgst = 0, igst = 0;
  for (const c of charges) {
    if (c.mode !== 'add_amount' || c.amount <= 0) continue;
    sub += c.amount;
    if (supplyType === 'inter_state') {
      igst += r2((c.amount * c.gstRatePct) / 100);
    } else {
      cgst += r2((c.amount * (c.gstRatePct / 2)) / 100);
      sgst += r2((c.amount * (c.gstRatePct / 2)) / 100);
    }
  }
  const chargesCgst = r2(cgst), chargesSgst = r2(sgst), chargesIgst = r2(igst);
  return {
    chargesSubtotal: r2(sub),
    chargesCgst, chargesSgst, chargesIgst,
    chargesTax: r2(chargesCgst + chargesSgst + chargesIgst),
  };
}

export function ChargesSection({
  charges,
  onChange,
}: {
  charges: ProposalCharge[];
  onChange: (c: ProposalCharge[]) => void;
}) {
  const at = (t: ChargeType): ProposalCharge =>
    charges.find((c) => c.type === t) ?? { type: t, mode: 'inclusive', amount: 0, gstRatePct: 0, show: false };

  const emit = (updated: ProposalCharge) =>
    onChange(normalizeCharges(CHARGE_TYPES.map((t) => (t === updated.type ? updated : at(t)))));

  return (
    <div className="mt-6">
      <h3 className="mb-2 text-xs font-bold tracking-wide text-gray-500">CHARGES</h3>
      <div className="space-y-2">
        {CHARGE_TYPES.map((t) => {
          const c = at(t);
          const priced = c.mode === 'add_amount';
          return (
            <div key={t} className="rounded border border-gray-200 p-3">
              <div className="mb-2 text-sm font-semibold">{CHARGE_LABELS[t]}</div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid={`charge-mode-${t}`}
                  value={c.mode}
                  onChange={(e) => {
                    const mode = e.target.value as ChargeMode;
                    const nowPriced = mode === 'add_amount';
                    emit({
                      ...c,
                      mode,
                      amount: nowPriced ? c.amount : 0,
                      gstRatePct: nowPriced ? (c.gstRatePct || 18) : 0,
                      show: nowPriced ? true : c.show,
                    });
                  }}
                  className="rounded border border-gray-200 px-2 py-1.5 text-sm"
                >
                  {CHARGE_MODES.map((m) => (
                    <option key={m} value={m}>{MODE_LABELS[m]}</option>
                  ))}
                </select>

                {priced ? (
                  <>
                    <input
                      data-testid={`charge-amount-${t}`}
                      type="number"
                      min={0}
                      value={c.amount || ''}
                      onChange={(e) => emit({ ...c, amount: posNum(e.target.value) })}
                      placeholder="Amount (ex-GST)"
                      className="w-40 rounded border border-gray-200 px-2 py-1.5 text-sm"
                    />
                    <select
                      data-testid={`charge-gst-${t}`}
                      value={c.gstRatePct}
                      onChange={(e) => emit({ ...c, gstRatePct: Number(e.target.value) })}
                      className="rounded border border-gray-200 px-2 py-1.5 text-sm"
                    >
                      {CHARGE_GST_RATES.map((r) => (
                        <option key={r} value={r}>{r}% GST</option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-500">Priced → always printed. Discounts never apply.</span>
                  </>
                ) : (
                  <label className="flex items-center gap-1.5 text-sm text-gray-600">
                    <input
                      data-testid={`charge-show-${t}`}
                      type="checkbox"
                      checked={c.show}
                      onChange={(e) => emit({ ...c, show: e.target.checked })}
                    />
                    Show on proposal
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
